import { ReactElement } from 'react';
import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import { IssueTranslationKeys } from 'intl/keys';
import { versionStateLabelKey } from 'intl/helpers/documentation';
import {
  isDocumentationLinkType,
  isLinkedDocumentationType,
  LinkedDocumentationType,
} from 'shared/domain/issue/linkedDocumentation';
import { formatVersionNumber, isOutdatedVersionState } from 'shared/domain/documentation/versionState';
import type { LinkableVersion, LinkTargetChoice } from './model';
import { linkTargetChooserSx, versionSelectSx } from './styles';

interface LinkTargetChooserProps {
  versions: LinkableVersion[];
  isDocumentationLinked: boolean;
  choice: LinkTargetChoice;
  onChange: (choice: LinkTargetChoice) => void;
}

/** "Link with": the documentation (always its current version) or a chosen version (PT-7117). */
export function LinkTargetChooser({
  versions,
  isDocumentationLinked,
  choice,
  onChange,
}: LinkTargetChooserProps): ReactElement {
  const intl = useIntl();
  const translate = (key: Parameters<typeof formatTranslation>[1]): string => formatTranslation(intl, key);
  // Most versions are outdated: only the current and the withdrawn ones are pointed out.
  const versionLabel = ({ number, name, state }: LinkableVersion): string =>
    [
      formatVersionNumber(number),
      name,
      isOutdatedVersionState(state) ? undefined : `(${translate(versionStateLabelKey(state))})`,
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <Box sx={linkTargetChooserSx} data-qa='import-from-documentation-link-target'>
      <Typography variant='subtitle2'>{translate(IssueTranslationKeys.linkedDocumentationLinkWith)}</Typography>
      <RadioGroup
        value={choice.type}
        onChange={(_, type) => {
          if (isLinkedDocumentationType(type)) {
            onChange({ ...choice, type });
          }
        }}
      >
        <FormControlLabel
          value={LinkedDocumentationType.documentation}
          control={<Radio size='small' />}
          disabled={isDocumentationLinked}
          label={translate(IssueTranslationKeys.linkedDocumentationLinkToDocumentation)}
        />
        <FormControlLabel
          value={LinkedDocumentationType.version}
          control={<Radio size='small' />}
          label={translate(IssueTranslationKeys.linkedDocumentationLinkToVersion)}
        />
      </RadioGroup>
      {!isDocumentationLinkType(choice.type) && (
        <Select
          size='small'
          sx={versionSelectSx}
          value={choice.versionId ?? ''}
          onChange={(event) => onChange({ ...choice, versionId: event.target.value || undefined })}
        >
          {versions.map((version) => (
            <MenuItem key={version.versionId} value={version.versionId} disabled={version.isLinked}>
              {versionLabel(version)}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
}
